export interface RunSelectionRun {
  id: string;
  status: string;
  createdAt: string;
  name?: string | null;
  runSettings: {
    workflowId: string;
  };
}

export type ReceiptValidity = "valid" | "invalid" | "unknown";
export type ReceiptValidityByRunId = Readonly<Record<string, ReceiptValidity>>;

export function parseRunTimestamp(iso: string): number | null {
  const raw = iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const timestamp = new Date(raw).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function compareRunsNewestFirst<Run extends RunSelectionRun>(a: Run, b: Run): number {
  const compareIds = () => (a.id === b.id ? 0 : a.id < b.id ? -1 : 1);
  const aTimestamp = parseRunTimestamp(a.createdAt);
  const bTimestamp = parseRunTimestamp(b.createdAt);
  if (aTimestamp === null && bTimestamp === null) return compareIds();
  if (aTimestamp === null) return 1;
  if (bTimestamp === null) return -1;
  if (aTimestamp !== bTimestamp) return bTimestamp - aTimestamp;
  return compareIds();
}

export function sortWorkflowRuns<Run extends RunSelectionRun>(
  runs: Run[],
  workflowId: string
): Run[] {
  return runs
    .filter((run) => run.runSettings.workflowId === workflowId)
    .sort(compareRunsNewestFirst);
}

export function sortCompletedWorkflowRuns<Run extends RunSelectionRun>(
  runs: Run[],
  workflowId: string,
  completedStatus: string
): Run[] {
  return sortWorkflowRuns(runs, workflowId).filter((run) => run.status === completedStatus);
}

export function findRecommendedRun<Run extends RunSelectionRun>(
  completedRuns: Run[],
  receiptValidityByRunId: ReceiptValidityByRunId
): Run | null {
  return (
    completedRuns.find(
      (run) =>
        receiptValidityByRunId[run.id] === "valid" && parseRunTimestamp(run.createdAt) !== null
    ) ?? null
  );
}

export function resolveSelectedRunId<Run extends RunSelectionRun>(
  selectedRunId: string | null,
  sortedRuns: Run[]
): string | null {
  if (sortedRuns.length === 0) {
    return null;
  }

  if (selectedRunId === null || !sortedRuns.some((run) => run.id === selectedRunId)) {
    return sortedRuns[0].id;
  }

  return selectedRunId;
}

export function resolveSelectedRun<Run extends RunSelectionRun>(
  selectedRunId: string | null,
  sortedRuns: Run[]
): Run | null {
  return sortedRuns.find((run) => run.id === selectedRunId) ?? sortedRuns[0] ?? null;
}

function to12h(hours: number, minutes: number): string {
  const period = hours >= 12 ? "PM" : "AM";
  const h = hours % 12 || 12;
  return `${h}:${String(minutes).padStart(2, "0")} ${period}`;
}

export function formatRunDate(iso: string): string {
  const timestamp = parseRunTimestamp(iso);
  if (timestamp === null) return "Time unavailable";
  const date = new Date(timestamp);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy} ${to12h(date.getHours(), date.getMinutes())}`;
}

export function formatRunOptionLabel(run: Pick<RunSelectionRun, "createdAt" | "name">): string {
  const date = formatRunDate(run.createdAt);
  const name = run.name?.trim();
  return name ? `${date} — ${name}` : date;
}

export function formatRunAvailability(status: string, receiptValidity: ReceiptValidity): string {
  if (status === "RUN_COMPLETED") {
    if (receiptValidity === "valid") return "Receipt validated";
    if (receiptValidity === "invalid") return "Receipt invalid";
    return "Receipt not checked";
  }

  const labels: Record<string, string> = {
    TO_RUN: "Waiting to run",
    RUN_TRANSFORMS_RUNNING: "Preparing inputs",
    RUN_IN_PROGRESS: "Run in progress",
    RUN_FAILED: "Run failed",
    CANCEL_COMPLETED: "Run cancelled",
    CANCEL_IN_PROGRESS: "Cancellation in progress",
    CANCEL_FAILED: "Cancellation failed",
  };
  return labels[status] ?? "Run unavailable";
}

export function isPlanningReceiptValidationError(error: Error): boolean {
  const message = error.message;
  // useViewTransform currently exposes only Error, not a typed failure code.
  // Keep this allowlist limited to deterministic contract errors emitted by
  // lmnt_replenishment_results so transient runtime failures stay unchecked.
  const validationPrefixes = [
    "A completed LMNT planning receipt",
    "Unsupported LMNT planning receipt version",
    "LMNT v2 receipt",
    "LMNT stage_results",
    "LMNT stage ",
    "LMNT selected stage ",
    "LMNT run exception ",
    "LMNT exception artifact ",
    "LMNT replenishment result",
  ];
  if (validationPrefixes.some((prefix) => message.startsWith(prefix))) return true;

  return /^LMNT (?:supply_walk|plan_lines|lineage|exceptions) artifact\b/.test(message);
}
